'use client';

import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useEffect, useRef } from 'react';

export interface PgIntroAData {
  bg_color?: string; // 背景颜色
  type?: number; // 2=图片, 其他=视频
  image?: string; // 图片或视频URL
  title_config?: {
    title?: string; // 标题文字
    title_color?: string; // 标题颜色
  };
  description_config?: {
    description?: string; // 描述文字
    description_color?: string; // 描述颜色
  };
}

interface PgIntroAProps {
  id?: string;
  pgIntroData: PgIntroAData;
  className?: string;
}

export default function PgIntroA({ id, pgIntroData, className = '' }: PgIntroAProps) {
  const bannerVideoRef = useRef<HTMLVideoElement>(null);

  // 视频自动播放
  useEffect(() => {
    if (bannerVideoRef.current && pgIntroData.type !== 2) {
      bannerVideoRef.current.play().catch(() => {
        // 处理自动播放失败的情况
      });
    }
  }, [pgIntroData.type]);

  if (!pgIntroData) {
    return null;
  }

  const containerStyle = {
    backgroundColor: pgIntroData.bg_color || '#ffffff',
  };

  return (
    <div id={id} className={`intro-part w-full ${className}`} style={containerStyle}>
      {/* PC端布局 */}
      <WebOnly>
        <div className="flex justify-between items-center w-full px-[40px] md:px-[70px] lg:px-[100px] xl:px-[130px] 2xl:px-[160px] py-[20px] md:py-[40px] xl:py-[60px] 2xl:py-[80px] gap-[60px] md:gap-[100px] lg:gap-[140px] xl:gap-[160px] 2xl:gap-[160px]">
          {/* 左侧文字内容 */}
          <div className="flex-1 w-full">
            {/* 标题 */}
            {pgIntroData.title_config?.title && (
              <h2
                className="text-[20px] md:text-[28px] lg:text-[36px] xl:text-[40px] 2xl:text-[56px] leading-[24px] md:leading-[32px] lg:leading-[42px] xl:leading-[48px] 2xl:leading-[67px] font-bb mb-[16px] lg:mb-[20px] xl:mb-[22px] 2xl:mb-[24px]"
                style={{
                  color: pgIntroData.title_config.title_color || '#282828',
                }}
              >
                {pgIntroData.title_config.title}
              </h2>
            )}

            {/* 描述 */}
            {pgIntroData.description_config?.description && (
              <h3
                className="text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px] 2xl:text-[24px] leading-[18px] md:leading-[20px] lg:leading-[22px] xl:leading-[24px] 2xl:leading-[28px]"
                style={{
                  color: pgIntroData.description_config.description_color || '#282828',
                }}
              >
                {pgIntroData.description_config.description}
              </h3>
            )}
          </div>

          {/* 右侧图片/视频 */}
          <div className="w-[300px] md:w-[400px] lg:w-[480px] xl:w-[500px] 2xl:w-[640px] flex-shrink-0">
            {pgIntroData.type === 2 ? (
              // 图片
              <Image
                src={pgIntroData.image || ''}
                alt="prabal gurung"
                width={640}
                height={400}
                className="w-full h-auto"
              />
            ) : (
              // 视频
              <div className="video-slide-out w-full h-full">
                <video
                  ref={bannerVideoRef}
                  className="video-slide block w-full h-full object-cover object-center"
                  loop
                  muted
                  playsInline
                  preload="auto"
                >
                  <source src={pgIntroData.image} />
                </video>
              </div>
            )}
          </div>
        </div>
      </WebOnly>

      {/* 移动端布局 */}
      <WapOnly>
        <div className="w-full px-[27px] py-[40px]">
          {/* 文字内容在上方 */}
          <div className="word-part mb-[16px]">
            {/* 标题 */}
            {pgIntroData.title_config?.title && (
              <h2
                className="text-[24px] leading-[29px] font-bb text-center mb-[8px]"
                style={{
                  color: pgIntroData.title_config.title_color || '#282828',
                }}
              >
                {pgIntroData.title_config.title}
              </h2>
            )}

            {/* 描述 */}
            {pgIntroData.description_config?.description && (
              <h3
                className="text-[13px] leading-[20px] text-center"
                style={{
                  color: pgIntroData.description_config.description_color || '#282828',
                }}
              >
                {pgIntroData.description_config.description}
              </h3>
            )}
          </div>

          {/* 图片/视频在下方 */}
          <div className="img-part relative rounded-[12px] overflow-hidden">
            {pgIntroData.type === 2 ? (
              // 图片
              <Image
                src={pgIntroData.image || ''}
                alt="prabal gurung"
                width={375}
                height={250}
                className="w-full h-auto"
              />
            ) : (
              // 视频
              <div className="video-slide-out w-full h-full">
                <video
                  ref={bannerVideoRef}
                  className="video-slide block w-full h-full object-cover object-center"
                  loop
                  muted
                  playsInline
                  preload="auto"
                >
                  <source src={pgIntroData.image} />
                </video>
              </div>
            )}
          </div>
        </div>
      </WapOnly>
    </div>
  );
}
